import { useStyles, Button, Label, MemberTile } from '@archipro-design/aria';
import type { UIMatch } from '@remix-run/react';
import { useMatches, useSearchParams } from '@remix-run/react';
import * as S from './TeamMembers.style';

import React, { useState } from 'react';

import type { TeamMembersResult } from './TeamMembers';
import type { TeamMemberView } from '@modules/professional/type';
import { getMemberPlaceholderImage } from './TeamMembers';
import { ClientOnly } from 'remix-utils';
import {
    INFO_PAGE_MATCH_ID,
    INFO_PAGE_MATCH_ID_EDITMODE,
} from '@modules/professional/config/constants';
import { useTracker } from '@archipro-website/tracker';
import { useConfigContext } from '@archipro-website/config/bindings/react';
import TeamMemberDetailsModalUpdated from './TeamMemberDetailsModalUpdated';
import type { APHandle } from '~/modules/root';
import type { ProfessionalTeamMemberDetailsLoaderData } from '~/modules/professional/page/TeamMemberDetailsPage';

export const TeamMembersMobile: React.FC<
    TeamMembersResult & { sectionTitle: string }
> = ({ teamMembers, pageInfo, sectionTitle }) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const { hasNextPage } = pageInfo;

    const { css } = useStyles();
    const matches = useMatches();
    const tracker = useTracker();

    const match = matches[matches.length - 1] as UIMatch<
        ProfessionalTeamMemberDetailsLoaderData,
        APHandle
    >;

    const isDialogRoute = match?.handle?.isDialogRoute;
    const memberProfileID = match?.data?.memberID;
    const infoPagePath =
        matches.find((match) =>
            [INFO_PAGE_MATCH_ID, INFO_PAGE_MATCH_ID_EDITMODE].includes(
                match.id,
            ),
        )?.pathname ?? '/';

    const loadMoreTeamMembers = () => {
        tracker.log('ProfessionalAllTeamClick', {
            url: new URL(window.location.href),
            targetTracker: 'archiproTracker',
        });

        setSearchParams(
            {
                ...searchParams,
                teamPage: ((pageInfo?.currentPage ?? 1) + 1).toString(),
            },
            {
                replace: true,
                preventScrollReset: true,
            },
        );
    };

    return (
        <div className={css(S.TeamMembersMobile)}>
            {sectionTitle && (
                <Label
                    className={css(S.TeamMembersSectionTitleMobile)}
                    variant="01"
                >
                    {sectionTitle}
                </Label>
            )}
            <div className={css(S.TeamMemberGridMobile)}>
                {teamMembers.map((member) => {
                    return (
                        <MemberMobile
                            key={member.id}
                            member={member}
                            basePath={infoPagePath}
                            modalIsOpen={
                                isDialogRoute && member.id === memberProfileID
                            }
                        />
                    );
                })}
            </div>
            {hasNextPage && (
                <div className={css(S.ViewAll)}>
                    <Button
                        transparent={true}
                        onClick={loadMoreTeamMembers}
                        size={18}
                        variables={{
                            buttonPadding: 0,
                        }}
                    >
                        VIEW MORE
                    </Button>
                </div>
            )}
        </div>
    );
};

const MemberMobile: React.FC<{
    member: TeamMemberView;
    basePath: string;
    modalIsOpen?: boolean;
    disablePhotoViewer?: boolean;
}> = ({ member, modalIsOpen = false }) => {
    const { css } = useStyles();
    const config = useConfigContext();
    const { detailsPageImageSrc, name, jobTitle } = member;
    const [modalOpen, setModalOpen] = useState(modalIsOpen);
    const imageProps = {
        src: detailsPageImageSrc,
        width: 186,
        height: 223,
        unoptimized:
            detailsPageImageSrc ===
            getMemberPlaceholderImage(config.assetsBaseUrl)
                ? true
                : undefined,
    };
    const jobTitleProps = {
        maximumLines: 2,
        children: jobTitle ?? '',
    };

    return (
        <div>
            <ClientOnly fallback={null}>
                {() => (
                    <TeamMemberDetailsModalUpdated
                        member={member}
                        website={member.website ?? ''}
                        toggleModal={() => {
                            setModalOpen(false);
                        }}
                        isOpen={modalOpen}
                    />
                )}
            </ClientOnly>
            <MemberTile
                image={{
                    ...imageProps,
                    layout: 'fill',
                    alt: name,
                }}
                onClick={() => setModalOpen(true)}
                name={name}
                jobTitle={jobTitleProps}
                onToggleDetails={() => true}
                className={css(S.MemberTileMobile)}
                variables={{
                    tileWidth: 186,
                    tileHeight: 223,
                }}
            />
        </div>
    );
};
